import { Stack } from "@mui/material";
import { m } from "framer-motion";
import { useContext, useEffect } from "react";
import { FormProvider } from "src/components/hook-form";
import useLocales from "src/hooks/useLocales";
import { isValidToken } from "src/utils/jwt";

import ThemeContext from "../Products/context";
import AuthNotValid from "./AuthNotValid";
import Context from "./context";
import FormComponents from "./DynamicForm";
import { useDynamicForm } from "./hooks/useDynamicForm";
import UnexpectedError from "./UnexpectedError";

interface DynamicFormWrapperProps {}

const DynamicFormWrapper: React.FC<DynamicFormWrapperProps> = () => {
	const { params, selectedValue, onChangeValue, customConnectProps, product, entity } =
		useContext(Context);
	const { state } = useContext(ThemeContext);
	const { translate } = useLocales();

	const {
		methods,
		onSubmit,
		errorMessage,
		isSuccess,
		isError,
		isLoading,
		totpNeeded,
		userConfirmationNeeded,
		isUnexpectedError
	} = useDynamicForm({
		fields: params,
		fieldSelected: selectedValue,
		isProduct: !!product
	});

	const selector = params.find((field) => field.type === "select" || field.type === "radio");
	const showSelector = selector && !totpNeeded && !userConfirmationNeeded && !isSuccess;
	const isAuthValid = !!product || isValidToken(state.authToken);

	useEffect(() => {
		if (totpNeeded || userConfirmationNeeded) methods.reset();
	}, [totpNeeded, userConfirmationNeeded]);

	useEffect(() => {
		if (isError) methods.reset();
	}, [isError]);

	const onChangeSelect = (value: string) => {
		onChangeValue(value);
		methods.reset({ password: "", identifier: "" });
	};

	if (!isAuthValid && !isLoading) return <AuthNotValid />;

	if (isUnexpectedError && !isLoading)
		return <UnexpectedError message={translate("connect_panel.default_error")} />;

	return (
		<FormProvider methods={methods} onSubmit={onSubmit}>
			<Stack
				gap={2}
				component={m.div}
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.6 }}
			>
				<FormComponents
					errorMessage={errorMessage}
					isSuccess={isSuccess}
					showSelector={showSelector}
					entity={entity}
					totpNeeded={totpNeeded}
					userConfirmationNeeded={userConfirmationNeeded}
					selectedValue={selectedValue}
					onChangeSelect={onChangeSelect}
					showSaveCheckbox={customConnectProps?.showSaveCheckbox}
					isLoading={isLoading}
					control={methods.control}
				/>
			</Stack>
		</FormProvider>
	);
};

export default DynamicFormWrapper;
